import { parentsOf } from '../construction';

import { type SceneDocument } from './document';
import { type EntityId } from './ids';

/**
 * Which solid a construction ultimately came from.
 *
 * ## Why anything needs to ask
 *
 * Because a plane is named the way a problem names one — `plane S A C` — and nobody says which
 * figure those corners belong to. Asking would be asking the author to repeat something they
 * have just said, in the script and with the mouse alike.
 *
 * So it is worked out. Walking back through {@link parentsOf} answers for a corner, for the
 * midpoint of two corners, for the foot of a perpendicular, and for a point built on a point
 * built on a corner — without a case for any of them.
 *
 * ## Why disagreement is an error rather than a guess
 *
 * Three points from two different solids name a plane that cuts neither. Picking one of them
 * would draw a section of a figure the user did not mean, and it would look entirely plausible.
 * `null` is the honest answer and the caller reports it.
 */
export function solidBehind(document: SceneDocument, id: EntityId): EntityId | null {
  const seen = new Set<EntityId>();
  const queue = [id];

  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined || seen.has(current)) continue;
    seen.add(current);

    const entity = document.entities.get(current);
    if (entity === undefined) continue;
    if (entity.source.op === 'solid-figure' || entity.source.op === 'solid') return current;
    // A plane already knows; taking its answer saves walking the whole construction again.
    if ('solid' in entity.source) return entity.source.solid;

    queue.push(...parentsOf(entity.source));
  }
  return null;
}

/**
 * The one solid every one of these came from, or `null`.
 *
 * `null` when any of them traces to no solid, and `null` when they do not all trace to the same
 * one — see above. Entities that trace nowhere are skipped rather than refused, so a plane named
 * partly by a corner and partly by a point built on it still answers.
 */
export function solidBehindAll(document: SceneDocument, ids: readonly EntityId[]): EntityId | null {
  let agreed: EntityId | null = null;

  for (const id of ids) {
    const found = solidBehind(document, id);
    if (found === null) continue;
    if (agreed !== null && agreed !== found) return null;
    agreed = found;
  }
  return agreed;
}
